import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { Anchor, CloseButton, Drawer, Flex, ScrollArea, Stack, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRef } from "react";
import { useAnalytics } from "@/analytics";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import type { ChallengeInfo } from "@/sales-tools/challenges/api/getChallengesForEmployee";
import { useChallenges } from "@/sales-tools/challenges/api/useChallenges";
import { Challenge } from "@/sales-tools/challenges/Challenge";
import { getMetricsDateRange } from "@/sales-tools/leaderboard/utils/getMetricsDateRange";
import { ChecklistSummary } from "@/sales-tools/metrics/checklist-summary/ChecklistSummary";
import { PerformanceMetrics } from "@/sales-tools/metrics/components/PerformanceMetrics";
import { TrophyCaseCard } from "@/sales-tools/metrics/components/TrophyCaseCard";
import { LeaderboardWidget } from "../../leaderboard/components/LeaderboardWidget";
import styles from "./WidgetContainer.module.css";

export function WidgetContainer() {
    const sidePanelRef = useRef<HTMLDivElement>(null);

    const {
        features: {
            isChallengeEnabled: isChallengeEnabledForExpert,
            isExpertPerformanceDashboardEnabled,
            isChecklistSummaryEnabled,
        },
    } = useFeatures<WorkspaceFeatures>();
    const { isChallengesEnabled: isChallengesEnabledForPartner } = usePartnerFeatureConfigured();
    const { leaderboards } = usePartnerConfig();
    const [opened, { open, close }] = useDisclosure(false);
    const { dispatcher } = useAnalytics();
    const { data: challengeData } = useChallenges();

    const handleClose = (source: string) => {
        void dispatcher.dispatchBusinessEvent("CloseExpertPerformanceDashboard", { source });
        close();
    };
    const handleOpen = (source: string) => {
        void dispatcher.dispatchBusinessEvent("OpenExpertPerformanceDashboard", { source });
        open();
    };

    function hasEnrollmentChallenge(challenge: ChallengeInfo | null | undefined) {
        if (challenge) {
            return challenge.challengeType === "solo" || challenge.challengeType === "team";
        }
    }

    const hasLeaderboards = leaderboards.length > 0;
    const shouldHideWidgetContainer = !hasLeaderboards && !isChallengeEnabledForExpert;

    if (shouldHideWidgetContainer) return undefined;

    const showLeftPanel =
        (isChallengeEnabledForExpert && challengeData?.some(hasEnrollmentChallenge)) || hasLeaderboards;

    const showStatsHeader = isExpertPerformanceDashboardEnabled;

    const onClick = () => {
        if (opened) handleClose("PayWidget");
        else if (showStatsHeader) {
            handleOpen("PayWidget");
        }
    };

    return (
        <Flex flex={1} className={styles.main} bg="dark.8" ref={sidePanelRef}>
            {showLeftPanel && (
                <Stack
                    bd={opened ? "1px solid #8537c5" : "none"}
                    flex={1}
                    gap={0}
                    data-testid="widget-container"
                    bg={opened ? "#271a34" : "dark.5"}
                >
                    <ScrollArea type="hover" scrollbarSize={0}>
                        {showStatsHeader && (
                            <Flex className={styles.widgetMain} direction="column" fz="xs" onClick={onClick}>
                                <Flex className={styles.inner}>
                                    <Text fw={700} lh="md">
                                        My stats
                                    </Text>
                                    <UnstyledButton variant="outline">
                                        <Flex align="center">
                                            <Anchor c="primary.1" size="xxs" underline="always">
                                                Show {opened ? "less" : "more"}
                                            </Anchor>
                                        </Flex>
                                    </UnstyledButton>
                                </Flex>
                            </Flex>
                        )}

                        {isChallengeEnabledForExpert && isChallengesEnabledForPartner && <Challenge />}
                        {hasLeaderboards && <LeaderboardWidget leaderboards={leaderboards} />}
                    </ScrollArea>
                </Stack>
            )}

            <Drawer
                opened={opened}
                withinPortal={false}
                onClose={() => handleClose("PayWidget")}
                withCloseButton={false}
                overlayProps={{
                    backgroundOpacity: 0,
                    blur: 0,
                }}
                transitionProps={{
                    duration: 0,
                }}
                withOverlay={false}
                styles={{
                    content: {
                        backgroundColor: "#14171C",
                        left: 0,
                        position: "absolute",
                        top: sidePanelRef.current?.getBoundingClientRect().top,
                        height: sidePanelRef.current?.getBoundingClientRect().height,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        border: "1px solid #8537c5",
                        borderLeft: "none",
                    },
                    body: {
                        padding: 0,
                    },
                }}
            >
                <Stack w="39.5rem" p="md" styles={{ root: { overflow: "auto" } }}>
                    <Stack pos="absolute" right="0.2rem" top=".1rem">
                        <CloseButton variant="transparent" ml="auto" onClick={() => handleClose("CloseButton")} />
                    </Stack>
                    <Flex justify="center">
                        <Stack w="100%">
                            {isExpertPerformanceDashboardEnabled && (
                                <>
                                    <Flex justify="space-between" mt="0.875rem">
                                        <Text size="xs">Week of {getMetricsDateRange()}</Text>
                                    </Flex>
                                    <PerformanceMetrics />
                                    <Stack w="24.438rem" pos="absolute" right="0.875rem" top="1.75rem" gap="1.25rem">
                                        <Text size="xxs" c="dark.2" ta="right">
                                            Heads up&mdash;this data is not official and might not represent final
                                            metrics.
                                        </Text>
                                        {isChecklistSummaryEnabled && <ChecklistSummary />}
                                        {isChallengeEnabledForExpert && isChallengesEnabledForPartner && (
                                            <TrophyCaseCard />
                                        )}
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Flex>
                </Stack>
            </Drawer>
        </Flex>
    );
}
